import * as React from "react"
import { Box, Flex, List, ListItem, Text } from "@chakra-ui/react"
import { useRecoilState, useRecoilValue } from "recoil"
import { brandingSelector } from "../../../recoil/atoms/room"
import { useTranslation } from "react-i18next"
import { roomLinksSelector } from "../../../recoil/atoms/roomLinks"
import { CopyIcon } from "../../common/icons"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { shareLinksModalAtom } from "../../../recoil/atoms/layout/share-links-modal"
import { IoClose } from "react-icons/io5"
import { Icon } from "../../common/icons"
import { ShareSocialMedia } from "./share-social-media"
import { replaceOpacity, getOpacity, isRGBAcolor, blendWithWhiteBackground } from "../../../util"
import MOLocation from "../../../util/MOLocation"
import { MODAL_Z_INDEX } from "../../../constants"
import useOnClickWithTouchHandler from "../../../hooks/useOnClickWithTouchHandler"
import useMeetingClient from "../../../hooks/useMeetingClient"

interface ShareLinksModalProps {
  isTablet: boolean
  isMobile?: boolean
}

export const ShareLinksModal: React.FC<ShareLinksModalProps> = ({
  isTablet,
  isMobile
}) => {
  const { isModerator, isBroadcaster, isAudience } = useLocalUserType()

  const roomStyles = useRecoilValue(brandingSelector)
  const meetingClient = useMeetingClient()
  const { t } = useTranslation()
  const currentUrl = MOLocation.getLocation().origin
  const { audienceHash, broadcasterHash } = useRecoilValue(roomLinksSelector)
  const [isShareLinksModal, setShareLinksModal] = useRecoilState(shareLinksModalAtom)

  // Store current user room url
  const currentRoomURL = React.useMemo(() => {
    return `${MOLocation.getLocation().origin}/${meetingClient.getRoomHash()}`
  }, [meetingClient])

  const links = React.useMemo(
    () => [
      {
        id: "link-1",
        text: "Moderator Link",
        link: currentRoomURL,
        enabled: isModerator
      },
      {
        id: "link-2",
        text: "Broadcaster Link",
        link: isBroadcaster
          ? currentRoomURL
          : `${currentUrl}/${broadcasterHash}`,
        enabled: isModerator || isBroadcaster
      },
      {
        id: "link-3",
        text: "Audience Link",
        link: isAudience ? currentRoomURL : `${currentUrl}/${audienceHash}`,
        enabled: true
      }
    ],
    [
      currentRoomURL,
      audienceHash,
      broadcasterHash,
      currentUrl,
      isAudience,
      isBroadcaster,
      isModerator
    ]
  )

  const closeModal = () => {
    setShareLinksModal(false)
  }

  const closeModalHandler = useOnClickWithTouchHandler(closeModal)

  const сolorBorder = React.useMemo<string>(() => {
    if (isRGBAcolor(roomStyles?.servicePanelElementBorder)) {
      return getOpacity(roomStyles?.servicePanelElementBorder) > 0.3
        ? replaceOpacity(
            roomStyles?.servicePanelElementBorder,
            getOpacity(roomStyles?.servicePanelElementBorder) - 0.3
          )
        : replaceOpacity(roomStyles?.servicePanelElementBorder, 0.1)
    }
    return `${roomStyles?.servicePanelElementBorder}b3` ?? "#28a745"
  }, [roomStyles?.servicePanelElementBorder])

  if(!isShareLinksModal) return null 

  return (
    <Box
      width="100vw"
      height="100vh"
      position="fixed"
      background="white"
      zIndex={MODAL_Z_INDEX}
      top="0"
      left="0"
      backgroundColor="rgba(255, 255, 255, 0.5)"
    >
      <Flex
        minW={isTablet ? "300px" : "550px"}
        width={isMobile ? "90%": `calc(min(550px, 92vw))`}
        position="absolute"
        top={"50%"}
        left="50%"
        padding="1rem 1rem 1.25rem 1rem"
        marginRight="-50%"
        transform="translate(-50%, -50%)"
        flexDirection="column"
        backgroundColor="rgba(255, 255, 255)"
        borderRadius="0.375rem"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={roomStyles.servicePanelElementBorder ?? "gray.300"}
        bg={blendWithWhiteBackground(roomStyles?.servicePanelElementModalBackground) ?? "white"}
        boxShadow={`0 0 15px ${
          roomStyles?.servicePanelElementBorder ?? "#e2e8f0"
        }`}
        color={roomStyles?.servicePanelElementPrimaryText ?? "black"}
        alignItems="center"
      >
        <div {...closeModalHandler}>
          <Icon
            src={IoClose}
            position="absolute"
            right="0.1rem"
            top="0.1rem"
            cursor={"pointer"}
            opacity={0.75}
            fontSize="1.5rem"
            zIndex="10000006"
            _hover={{ opacity: 1 }}
          />
        </div>
        <Text fontSize="1.5rem" fontWeight={"500"}>
          {t("Share link")}
        </Text>

        <List width={isMobile ? "100%" : "calc(min(422px, 100%))"}>
          {links.map(({ id, enabled, text, link }, index) =>
            !enabled ? null : (
              <ListItem
                key={id}
                alignItems={"center"}
                justifyContent="center"
                marginTop={index === 0 ? "8px" : "8px"}
              >
                <Text fontSize="0.9rem" fontWeight={"500"}>
                  {t(text)}
                  <Flex
                    backgroundColor={
                      roomStyles.servicePanelElementBackground ?? "gray"
                    }
                    justifyContent="space-between"
                    padding="0.4rem"
                    alignItems={"center"}
                    borderRadius="0.375rem"
                    border={`2px solid ${
                      сolorBorder ?? roomStyles?.servicePanelElementBorder
                    }`}
                  >
                    <Text marginLeft="0rem" lineHeight="1">
                      {link}
                    </Text>
                    <CopyIcon
                      textToCopy={link}
                      textForToast={t("Link copied to clipboard")}
                      otherProps={{
                        width: "30px",
                        height: "30px",
                        "aria-label": "copy-icon",
                        fontSize: "1rem"
                      }}
                    />
                  </Flex>
                </Text>
              </ListItem>
            )
          )}
        </List>

        <ShareSocialMedia isMobile={isMobile || isTablet} />
      </Flex>
    </Box>
  )
}
